import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import type { Observable } from 'rxjs';

/** One of the three per-field privacy settings (mirrors the backend `FieldVisibility`). */
export type FieldVisibility = 'alleNaboer' | 'kunStyret' | 'skjult';

/** The private fields shown for a viewer — hidden fields are absent, never null-valued. */
export type ProjectedFields = Record<string, string | null>;

export interface NeighbourRow {
  userId: string;
  displayName: string;
  unitLabel: string;
  nyINabolaget: boolean;
  avatarKey?: string | null;
  fields: ProjectedFields;
}

export interface NeighbourProfile {
  userId: string;
  displayName: string;
  unitLabel: string;
  nyINabolaget: boolean;
  avatarKey?: string | null;
  fields: ProjectedFields;
}

export interface HouseholdMember {
  name: string;
  type: 'voksen' | 'barn' | 'kjaeledyr';
  hasParentalConsent: boolean;
}

export interface OwnProfile {
  userId: string;
  displayName: string;
  unitLabel: string;
  avatarKey?: string | null;
  phone?: string | null;
  email?: string | null;
  birthday?: string | null;
  parking?: string | null;
  about?: string | null;
  emergencyContact?: string | null;
  household: HouseholdMember[];
  visibility: Record<string, FieldVisibility>;
  completionPercent: number;
  /** The user's chosen language (`nb` | `nn` | `en`), or null when they have not chosen — follows them across devices. */
  locale?: string | null;
}

export interface NaboerProfileUpdate {
  displayName?: string | null;
  phone?: string | null;
  email?: string | null;
  birthday?: string | null;
  parking?: string | null;
  about?: string | null;
  avatarKey?: string | null;
}

/**
 * Neighbour directory + own-profile client (`/api/naboer`, `/api/profil`). Hand-written
 * to mirror the Phase M3 backend contract. Privacy is enforced server-side: a hidden
 * field is simply absent from {@link ProjectedFields}, so there is no client-side
 * filtering path for secret data.
 */
@Injectable({ providedIn: 'root' })
export class NaboerService {
  private readonly http = inject(HttpClient);

  updateProfile(update: NaboerProfileUpdate): Observable<void> {
    return this.http.put<void>('/api/profil/me', update);
  }

  /** Replaces the per-field visibility map (per tenant). */
  updateVisibility(visibility: Record<string, FieldVisibility>): Observable<void> {
    return this.http.put<void>('/api/profil/me/visibility', { visibility });
  }

  /**
   * Stores the user's language choice on the profile so it follows them to other devices
   * (`null` clears it, falling back to the lag default / browser). The local switch is instant —
   * this call is the cross-device mirror.
   */
  updateLocale(locale: string | null): Observable<void> {
    return this.http.put<void>('/api/profil/me/sprak', { locale });
  }

}
